import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaRegClock } from "react-icons/fa";
import styles from "./ViewRemedial.module.css";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const OPTION_LETTERS = ["a", "b", "c", "d", "e", "f"];
const SUCCESS_REDIRECT_SECONDS = 10;

const draftKey = (remedialId, studentId) =>
  `remedial_draft__${remedialId}__${studentId}`;

function readDraft(remedialId, studentId) {
  if (!remedialId || !studentId) return null;
  try {
    const raw = localStorage.getItem(draftKey(remedialId, studentId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeDraft(remedialId, studentId, patch) {
  if (!remedialId || !studentId) return;
  try {
    const current = readDraft(remedialId, studentId) || {};
    const next = { ...current, ...patch };
    localStorage.setItem(
      draftKey(remedialId, studentId),
      JSON.stringify(next),
    );
  } catch {
    /* ignore */
  }
}

function clearDraftFor(remedialId, studentId) {
  if (!remedialId || !studentId) return;
  try {
    localStorage.removeItem(draftKey(remedialId, studentId));
  } catch {
    /* ignore */
  }
}

function ViewRemedialTest() {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const client_id = clientData?.client_id || "";
  const user_id = user?.id || "";
  const slug = String(user?.role?.slug || "").toLowerCase();
  const navigateUser =
    slug === "staff" ? "/user/assessmentList" : "/user/assessment";

  const remedialId = location?.state?.ID;
  const studentId = user_id;

  /* ===== State ===== */
  const [data, setData] = useState(null);

  const [answers, setAnswers] = useState(() => {
    const draft = readDraft(remedialId, studentId);
    return draft?.answers || {};
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [deadlineMs, setDeadlineMs] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const submitFiredRef = useRef(false);
  const autoSubmitFiredRef = useRef(false);
  const successCountdownRef = useRef(null);

  const [successCountdown, setSuccessCountdown] = useState(
    SUCCESS_REDIRECT_SECONDS,
  );

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: null,
  });

  const [submittedSuccess, setSubmittedSuccess] = useState({
    open: false,
    message: "",
    redirectPath: "",
    redirectState: null,
  });

  /* ===== Modal helpers ===== */
  const showModal = (type, message, onConfirm = null, title = "") => {
    setModalState({ open: true, type, title, message, onConfirm });
  };

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, open: false, onConfirm: null }));
  };

  const handleModalClose = useCallback(() => {
    if (submitting) return;
    closeModal();
  }, [submitting]);

  const handleModalConfirm = () => {
    const fn = modalState.onConfirm;
    closeModal();
    if (typeof fn === "function") fn();
  };

  /* ===== Persist answers to localStorage on every change ===== */
  useEffect(() => {
    writeDraft(remedialId, studentId, { answers });
  }, [answers, remedialId, studentId]);

  /* ===== Initial fetch ===== */
  useEffect(() => {
    if (remedialId && studentId) {
      fetchAssessment();
    } else {
      setLoadError(
        translations?.labels?.missingAssessmentInfo ||
        "Missing assessment or student information",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remedialId, studentId]);

  /* Builds the submission `answers` array. Always includes EVERY question
   * (with answer: null for unanswered). Question IDs come from, in order:
   *   1. an explicit list passed in (fresh API response)
   *   2. the current `data.questions` state
   *   3. the question IDs persisted in localStorage */
  const buildAnswersArray = (answersMap, questionListFromApi) => {
    const fromApi =
      Array.isArray(questionListFromApi) && questionListFromApi.length
        ? questionListFromApi.map((q) => q.id)
        : null;

    const fromState =
      Array.isArray(data?.questions) && data.questions.length
        ? data.questions.map((q) => q.id)
        : null;

    const fromDraft = (() => {
      const d = readDraft(remedialId, studentId);
      return Array.isArray(d?.questionIds) && d.questionIds.length
        ? d.questionIds
        : null;
    })();

    const questionIds = fromApi || fromState || fromDraft || [];

    return questionIds.map((qid) => ({
      question_id: qid,
      answer: answersMap?.[qid] ?? null,
    }));
  };

  const submitDraftFromLocalStorage = async (apiRemedialData) => {
    const draft = readDraft(remedialId, studentId);
    const draftAnswers = draft?.answers || {};

    const submissionAnswers = buildAnswersArray(
      draftAnswers,
      apiRemedialData?.questions,
    );

    if (submissionAnswers.length === 0) {
      clearDraftFor(remedialId, studentId);
      return;
    }

    try {
      const payload = {
        school_id:
          apiRemedialData?.remedial?.school_id ||
          apiRemedialData?.assessment?.school_id ||
          school_id,
        student_id: studentId,
        client_id:
          apiRemedialData?.remedial?.client_id ||
          apiRemedialData?.assessment?.client_id ||
          client_id,
        remedial_id: remedialId,
        answers: submissionAnswers,
        submission_source: "auto",
        auto_submitted: true,
      };

      await AxiosInstance.post(
        `/book/student/submitRemedialAssessment`,
        payload,
      );
    } catch (recoverErr) {
      console.warn(
        "Draft recovery submit failed (likely already submitted):",
        recoverErr?.response?.data?.message || recoverErr?.message,
      );
    } finally {
      clearDraftFor(remedialId, studentId);
    }
  };

  const fetchAssessment = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const payload = {
        school_id,
        student_id: studentId,
        id: remedialId,
      };

      const response = await AxiosInstance.post(
        `/book/student/startRemedialAssessment`,
        payload,
      );

      const apiData = response?.data?.data || {};
      const apiMessage = response?.data?.message || "";

      const alreadyDone =
        apiData?.already_submitted === true ||
        apiData?.is_completed === true ||
        apiData?.submitted === true ||
        response?.data?.code === "ALREADY_SUBMITTED" ||
        /already (completed|submitted|attempted)/i.test(apiMessage);

      if (alreadyDone) {
        await submitDraftFromLocalStorage(apiData);

        submitFiredRef.current = true;

        setSubmittedSuccess({
          open: true,
          message:
            apiMessage ||
            translations?.labels?.assessmentSubmittedTitle ||
            "Your assessment has been submitted successfully.",
          redirectPath: "/user/viewStudentRemedialResult",
          redirectState: {
            ID: remedialId,
            STUDENTID: studentId,
            ASSIGNMENTID: remedialId,
            mode: "result",
            type: "remedial",
          },
        });
        return;
      }

      if (response?.data?.status) {
        const payloadData = response.data.data;
        setData(payloadData);

        /* Persist question IDs so beacon / recovery / auto-submit paths
         * always have them, even when React state is stale. */
        if (Array.isArray(payloadData?.questions)) {
          writeDraft(remedialId, studentId, {
            questionIds: payloadData.questions.map((q) => q.id),
          });
        }

        const a = payloadData?.remedial || payloadData?.assessment || {};
        const durationMin = Number(a.duration ?? a.time_duration);

        if (Number.isFinite(durationMin) && durationMin > 0) {
          const existingDraft = readDraft(remedialId, studentId);
          let computed;

          if (
            existingDraft?.deadlineMs &&
            Number.isFinite(existingDraft.deadlineMs)
          ) {
            computed = existingDraft.deadlineMs;
          } else {
            const attendedAt = a.attended_at
              ? new Date(a.attended_at)
              : a.started_at
                ? new Date(a.started_at)
                : new Date();
            computed = attendedAt.getTime() + durationMin * 60 * 1000;
            writeDraft(remedialId, studentId, { deadlineMs: computed });
          }

          setDeadlineMs(computed);
          setSecondsLeft(
            Math.max(0, Math.floor((computed - Date.now()) / 1000)),
          );
        }
      } else {
        setLoadError(
          apiMessage ||
          translations?.labels?.failedToLoadAssessment ||
          "Failed to load assessment",
        );
      }
    } catch (err) {
      console.error("Error loading assessment:", err);
      setLoadError(
        err?.response?.data?.message ||
        err?.message ||
        translations?.labels?.networkError ||
        "Network error. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* ===== Auto-submit on tab close / refresh ===== */
  useEffect(() => {
    if (!remedialId || !studentId)
      return undefined;

    const fireBeacon = () => {
      if (submitFiredRef.current) return;

      const draft = readDraft(remedialId, studentId);

      const draftAnswers = draft?.answers || {};

      const submissionAnswers = buildAnswersArray(draftAnswers);

      if (submissionAnswers.length === 0)
        return;

      const payload = {
        school_id:
          data?.remedial?.school_id ||
          data?.assessment?.school_id ||
          school_id,

        student_id: studentId,

        client_id:
          data?.remedial?.client_id ||
          data?.assessment?.client_id ||
          client_id,

        remedial_id: remedialId,

        answers: submissionAnswers,

        submission_source: "auto",

        auto_submitted: true,
      };

      const baseURL = (
        AxiosInstance.defaults?.baseURL || ""
      ).replace(/\/$/, "");

      const url = `${baseURL}/book/student/submitRemedialAssessment`;

      try {
        const blob = new Blob(
          [JSON.stringify(payload)],
          {
            type: "application/json",
          }
        );

        navigator.sendBeacon(url, blob);

        submitFiredRef.current = true;
      } catch {
        //
      }
    };

    // ONLY browser close / refresh
    window.addEventListener(
      "beforeunload",
      fireBeacon
    );

    window.addEventListener(
      "pagehide",
      fireBeacon
    );

    return () => {
      window.removeEventListener(
        "beforeunload",
        fireBeacon
      );

      window.removeEventListener(
        "pagehide",
        fireBeacon
      );
    };
  }, [
    remedialId,
    studentId,
    data,
    school_id,
    client_id,
  ]);

  /* ===== Timer tick ===== */
  useEffect(() => {
    if (!deadlineMs) return undefined;

    const tick = () => {
      const remaining = Math.max(
        0,
        Math.floor((deadlineMs - Date.now()) / 1000),
      );
      setSecondsLeft(remaining);
      return remaining;
    };

    if (tick() === 0) return undefined;
    const id = setInterval(() => {
      if (tick() === 0) clearInterval(id);
    }, 1000);

    return () => clearInterval(id);
  }, [deadlineMs]);

  const timerExpired = secondsLeft === 0;
  const isLocked = timerExpired || submitting;

  /* ===== Auto-submit on timer expiry ===== */
  useEffect(() => {
    if (
      timerExpired &&
      !autoSubmitFiredRef.current &&
      data?.questions?.length
    ) {
      autoSubmitFiredRef.current = true;

      const draft = readDraft(remedialId, studentId);
      const latestAnswers = draft?.answers || answers;

      doSubmit({ auto: true, answersOverride: latestAnswers });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerExpired, data]);

  /* ===== Success countdown ===== */
  useEffect(() => {
    if (submittedSuccess.open) {
      setSuccessCountdown(SUCCESS_REDIRECT_SECONDS);
      successCountdownRef.current = setInterval(() => {
        setSuccessCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(successCountdownRef.current);
            navigateToResult();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(successCountdownRef.current);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedSuccess.open]);

  const navigateToResult = useCallback(() => {
    if (successCountdownRef.current) {
      clearInterval(successCountdownRef.current);
    }
    const { redirectPath, redirectState } = submittedSuccess;
    if (redirectPath) {
      navigate(
        redirectPath,
        redirectState ? { state: redirectState } : undefined,
      );
    }
  }, [submittedSuccess, navigate]);

  /* ===== Handlers ===== */
  const handleSelectOption = (questionId, letter) => {
    if (isLocked) return;
    setAnswers((prev) => ({ ...prev, [questionId]: letter }));
  };

  const handleCancel = () => {
    if (isLocked) return;
    showModal(
      "confirm",
      translations?.labels?.cancelConfirmMessage ||
      "Are you sure you want to cancel? Your answers will be lost.",
      () => {
        clearDraftFor(remedialId, studentId);
        navigate(navigateUser, { state: { activeTab: 1 } });
      },
    );
  };

  const handleSubmit = () => {
    if (isLocked) return;
    if (!data?.questions?.length) return;
    showModal("confirm", "", () => doSubmit({ auto: false }), "submit");
  };

  /* Shared submit path — always sends every question, null for unanswered. */
  const doSubmit = useCallback(
    async ({ auto, answersOverride } = { auto: false }) => {
      if (submitting) return;
      setSubmitting(true);

      const answersToSend = answersOverride || answers;

      try {
        const submissionAnswers = buildAnswersArray(answersToSend);

        const payload = {
          school_id:
            data?.remedial?.school_id ||
            data?.assessment?.school_id ||
            school_id,
          student_id: studentId,
          client_id:
            data?.remedial?.client_id ||
            data?.assessment?.client_id ||
            client_id,
          remedial_id: remedialId,
          answers: submissionAnswers,
          submission_source: auto ? "auto" : "manual",
          auto_submitted: !!auto,
        };

        const response = await AxiosInstance.post(
          `/book/student/submitRemedialAssessment`,
          payload,
        );

        const ok =
          response?.data?.status === true || response?.data?.success === true;

        if (ok) {
          submitFiredRef.current = true;

          const resultData = response?.data?.data || {};

          clearDraftFor(remedialId, studentId);

          setSubmittedSuccess({
            open: true,
            message:
              response?.data?.message ||
              translations?.labels?.assessmentSubmittedTitle ||
              translations?.labels?.assessmentSubmittedSuccess ||
              "Your assessment has been submitted successfully.",
            redirectPath: "/user/viewStudentRemedialResult",
            redirectState: {
              ID: resultData.remedial_id || remedialId,
              STUDENTID: studentId,
              ASSIGNMENTID: remedialId,
              ROWDATA: resultData,
              mode: "result",
              type: "remedial",
            },
          });
        } else {
          showModal(
            "error",
            response?.data?.message ||
            translations?.labels?.failedToSubmitAssessment ||
            "Failed to submit assessment",
          );
        }
      } catch (err) {
        console.error("Error submitting assessment:", err);
        showModal(
          "error",
          err?.response?.data?.message ||
          err?.message ||
          translations?.labels?.networkError ||
          "Network error. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      data,
      answers,
      remedialId,
      studentId,
      school_id,
      client_id,
      translations,
      submitting,
    ],
  );

  /* ===== Derived ===== */
  const answeredCount = useMemo(
    () => (data?.questions || []).filter((q) => answers[q.id]).length || 0,
    [data, answers],
  );
  const totalQuestions = data?.questions?.length || 0;

  const formatMmSs = (totalSec) => {
    if (totalSec == null) return null;
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return {
      m: String(m).padStart(2, "0"),
      s: String(s).padStart(2, "0"),
    };
  };
  const timeParts = formatMmSs(secondsLeft);

  const timerWarning =
    secondsLeft != null && secondsLeft <= 60 && secondsLeft > 0;
  const timerEnded = secondsLeft === 0;

  const getOptionText = (question, letter) => {
    if (!letter) return "";
    const key = `option_${letter.toLowerCase()}`;
    return question[key];
  };

  /* ===== Modal title & content ===== */
  const isSubmitConfirm =
    modalState.type === "confirm" && modalState.title === "submit";

  const confirmHeading = (
    translations?.labels?.answeredOutOfTotal ||
    "You have answered {answered} out of {total} questions"
  )
    .replace("{answered}", String(answeredCount))
    .replace("{total}", String(totalQuestions));

  const confirmBody =
    translations?.labels?.submitConfirmMessage ||
    "Please review your answers carefully before submitting. Once submitted, you will not be able to make any further changes.";

  const modalTitle = (() => {
    if (isSubmitConfirm) return confirmHeading;
    if (modalState.title && modalState.title !== "submit")
      return modalState.title;
    if (modalState.type === "confirm") {
      return translations?.labels?.areYouSure || "Are you sure?";
    }
    return translations?.labels?.modalFailedTitle || "Failed";
  })();

  const modalMessage = isSubmitConfirm ? (
    <>
      <div>{confirmBody}</div>
      {answeredCount < totalQuestions && (
        <div className={styles.modalSubMessage}>
          {(
            translations?.labels?.unansweredWillBeSubmittedBlank ||
            "Note: {count} unanswered question(s) will be submitted blank."
          ).replace("{count}", String(totalQuestions - answeredCount))}
        </div>
      )}
    </>
  ) : (
    modalState.message
  );

  /* ===== Render guards ===== */
  if (loading) {
    return (
      <div className={styles.loaderWrapper}>
        <div className={styles.spinner}></div>
        <p>{translations?.labels?.loading || "Loading assessment..."}</p>
      </div>
    );
  }

  if (submittedSuccess.open) {
    return (
      <div className={styles.successWrapper}>
        <h1 className={styles.successTitle}>
          {submittedSuccess.message ||
            translations?.labels?.assessmentSubmittedTitle ||
            "Your assessment has been submitted successfully."}
        </h1>

        <p className={styles.successSubtitle}>
          {translations?.labels?.autoRedirectBefore ||
            "You will be automatically redirected to the Result page in "}
          <strong>{successCountdown}</strong>
          {translations?.labels?.autoRedirectMiddle || " seconds, or click "}
          <button
            type="button"
            onClick={navigateToResult}
            className={styles.successResultLink}
          >
            {translations?.labels?.viewResult || "View Result"}
          </button>
          {translations?.labels?.autoRedirectAfter || " to continue now."}
        </p>
      </div>
    );
  }

  if (loadError && !data) {
    return (
      <div className={styles.errorWrapper}>
        <p>⚠ {loadError}</p>
        <button onClick={() => navigate(-1)} className={styles.cancelButton}>
          {translations?.labels?.back || "Back"}
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { remedial: assessment, questions } = data;

  return (
    <div className={styles.container}>
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={modalTitle}
        message={modalMessage}
        onOk={
          modalState.type === "confirm" ? handleModalConfirm : handleModalClose
        }
        onClose={handleModalClose}
        showCancel={modalState.type === "confirm"}
        okText={
          modalState.type === "confirm"
            ? translations?.labels?.confirm || "Confirm"
            : translations?.labels?.ok || "OK"
        }
        cancelText={translations?.labels?.cancel || "Cancel"}
        okDisabled={submitting}
      />

      <div className={styles.topicInfo}>
        <div className={styles.topicLeft}>
          <p>
            <strong>{translations?.labels?.topic || "Topic"} :</strong>{" "}
            {assessment?.topic || "-"}
          </p>
          <p>
            <strong>{translations?.labels?.subTopic || "Sub Topic"} :</strong>{" "}
            {assessment?.sub_topic || "-"}
          </p>
        </div>

        <div className={styles.topicRight}>
          <p>
            <strong>{translations?.labels?.subject || "Subject"} :</strong>{" "}
            {assessment?.subject || "-"}
          </p>
          <p>
            <strong>{translations?.labels?.class || "Class"} :</strong>{" "}
            {assessment?.class_name || "-"}
            {assessment?.section_id ? ` - ${assessment.section_id}` : ""}
          </p>

          {timeParts && (
            <div
              className={`${styles.timerPill} ${timerEnded ? styles.timerPillEnded : ""
                } ${timerWarning ? styles.timerPillWarning : ""}`}
              role="timer"
              aria-live="polite"
              aria-atomic="true"
            >
              <FaRegClock className={styles.timerIcon} />
              <span className={styles.timerLabel}>
                {translations?.labels?.timeLeftToComplete ||
                  "Time left to complete:"}
              </span>
              <span className={styles.timerValue}>
                {timeParts.m} {translations?.labels?.minShort || "min"} :{" "}
                {timeParts.s} {translations?.labels?.secShort || "sec"}
              </span>
            </div>
          )}
        </div>
      </div>

      <div
        className={`${styles.questionsList} ${isLocked ? styles.questionsListLocked : ""
          }`}
      >
        {questions?.map((question, index) => {
          const selected = answers[question.id];

          return (
            <div key={question.id} className={styles.questionCard}>
              <p className={styles.questionText}>
                {index + 1}. {question.assessment_question}
              </p>

              <div className={styles.optionsGrid}>
                {OPTION_LETTERS.map((letter) => {
                  const optionText = getOptionText(question, letter);
                  if (!optionText) return null;

                  const isSelected = selected === letter;

                  return (
                    <label
                      key={letter}
                      className={`${styles.option} ${isSelected ? styles.optionSelected : ""
                        } ${isLocked ? styles.optionDisabled : ""}`}
                    >
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        value={letter}
                        checked={isSelected}
                        onChange={() => handleSelectOption(question.id, letter)}
                        className={styles.radioInput}
                        disabled={isLocked}
                      />
                      <span
                        className={`${styles.radio} ${isSelected ? styles.radioSelected : ""
                          }`}
                      ></span>
                      <span className={styles.optionLabel}>
                        {letter}) {optionText}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          onClick={handleCancel}
          className={styles.cancelButton}
          disabled={isLocked}
        >
          {translations?.labels?.cancel || "Cancel"}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className={styles.submitButton}
          disabled={isLocked}
        >
          {submitting
            ? translations?.labels?.submitting || "Submitting..."
            : translations?.labels?.submit || "Submit"}
        </button>
      </div>
    </div>
  );
}

export default ViewRemedialTest;